import React from 'react';
import { Building2 } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { UiSummaryCard, UiSummaryCards } from '../../components/ui/UiSummaryCard';

// Expense report tab — extracted from Reports.js. Presentational; data/handlers via ctx.
export default function ExpensesReport(ctx) {
  const { data, formatCurrency, openChartTable, expenseDailyTrend, openDrillDown, datePreset, formatRangeDateTick, COLORS } = ctx;
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <UiSummaryCards cols="grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
        <UiSummaryCard label="Total Expenses" value={formatCurrency(data?.summary?.total_expenses)} />
        <UiSummaryCard label="Entries" value={data?.summary?.total_entries || 0} />
        <UiSummaryCard label="Cash" value={formatCurrency(data?.summary?.cash_total)} />
        <UiSummaryCard label="Digital/Bank" value={formatCurrency(data?.summary?.digital_total)} />
        <UiSummaryCard label="Card" value={formatCurrency(data?.summary?.card_total)} />
      </UiSummaryCards>

      {/* Expense Trend */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base sm:text-lg font-semibold">Expense Trend</h3>
          <button onClick={() => openChartTable(
            'Expense Trend Data',
            expenseDailyTrend,
            ['label', 'total', 'count'],
            (row) => {
              const point = row.__raw || row;
              if (point.bucket_type === 'day') {
                openDrillDown('expense_daily', { date: point.bucket_value }, `Expenses on ${point.label}`);
              }
            }
          )} className="text-xs px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-full hover:bg-red-100 transition font-medium">View Table</button>
        </div>
        <div className="h-56 sm:h-72 cursor-pointer">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={expenseDailyTrend} onClick={(e) => {
              if (e && e.activePayload && e.activePayload[0]) {
                const point = e.activePayload[0].payload;
                if (point.bucket_type === 'day') {
                  openDrillDown('expense_daily', { date: point.bucket_value }, `Expenses on ${point.label}`);
                }
              }
            }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey={datePreset === 'year' ? 'bucket_value' : 'date'}
                tickFormatter={(value) => formatRangeDateTick(value, datePreset)}
                tick={{ fontSize: 10 }}
                interval={0}
                textAnchor="middle"
                height={36}
              />
              <YAxis />
              <Tooltip formatter={(v) => [formatCurrency(v), 'Expenses']} labelFormatter={(label, items) => items?.[0]?.payload?.label || label} />
              <Bar dataKey="total" fill="#ef4444" radius={[6, 6, 0, 0]} cursor="pointer" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Category */}
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold">By Category</h3>
            <button onClick={() => openChartTable(
              'Expense by Category',
              data?.by_category || [],
              ['category_display', 'total', 'count'],
              (row) => {
                const entry = row.__raw || row;
                openDrillDown('expense_category', { category: entry.category }, `Expenses: ${entry.category_display || entry.category}`);
              }
            )} className="text-xs px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-full hover:bg-red-100 transition font-medium">View Table</button>
          </div>
          <div className="h-48 sm:h-64 cursor-pointer">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data?.by_category || []} dataKey="total" nameKey="category_display" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name || ''} ${(percent * 100).toFixed(0)}%`}
                  onClick={(_, index) => {
                    const entry = (data?.by_category || [])[index];
                    if (entry) openDrillDown('expense_category', { category: entry.category }, `Expenses: ${entry.category_display || entry.category}`);
                  }}
                  cursor="pointer"
                >
                  {(data?.by_category || []).map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatCurrency(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-4">
            {(data?.by_category || []).map((item, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition"
                onClick={() => openDrillDown('expense_category', { category: item.category }, `Expenses: ${item.category_display || item.category}`)}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="text-sm font-medium capitalize">{item.category_display || item.category?.replace('_', ' ')}</span>
                  <span className="text-xs text-gray-500">({item.count})</span>
                </div>
                <span className="font-semibold text-red-600">{formatCurrency(item.total)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* By Department */}
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border">
          <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center gap-2">
            <Building2 size={18} /> By Department
          </h3>
          <div className="h-48 sm:h-64 cursor-pointer">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.by_department || []} layout="vertical" onClick={(e) => {
                if (e && e.activePayload && e.activePayload[0]) {
                  const payload = e.activePayload[0].payload;
                  const dept = payload.department__name;
                  openDrillDown('expense_department', { department: payload.department_filter || dept }, `Expenses: ${dept}`);
                }
              }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                <YAxis type="category" dataKey="department__name" width={100} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="total" radius={[0, 4, 4, 0]} cursor="pointer">
                  {(data?.by_department || []).map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-4">
            {(data?.by_department || []).map((item, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 transition"
                onClick={() => openDrillDown('expense_department', { department: item.department_filter || item.department__name }, `Expenses: ${item.department__name}`)}
              >
                <div className="flex items-center gap-2">
                  <Building2 size={14} className="text-purple-600" />
                  <span className="text-sm font-medium">{item.department__name}</span>
                  <span className="text-xs text-gray-500">({item.count} entries)</span>
                </div>
                <span className="font-semibold text-purple-700">{formatCurrency(item.total)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* By Payment Method */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border">
        <h3 className="text-base sm:text-lg font-semibold mb-4">By Payment Method</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {(data?.by_payment || []).map((item, i) => (
            <div key={i} className={`p-4 rounded-xl border ${
              item.payment_method === 'cash' ? 'bg-green-50 border-green-200' :
              item.payment_method === 'digital' ? 'bg-blue-50 border-blue-200' :
              'bg-purple-50 border-purple-200'
            }`}>
              <p className="text-sm text-gray-600 capitalize">{item.payment_method === 'digital' ? 'Digital/Bank' : item.payment_method}</p>
              <p className={`text-xl font-bold ${
                item.payment_method === 'cash' ? 'text-green-700' :
                item.payment_method === 'digital' ? 'text-blue-700' :
                'text-purple-700'
              }`}>{formatCurrency(item.total)}</p>
              <p className="text-xs text-gray-500">{item.count} entries</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
